import { DateFormat, formatDateRange, formatLongDate, TimeFormat } from "@nova/format"
import { GenericUpload } from "@nova/constants"
import { InfraredFlight, OrthoFlight } from "@nova/protobuf"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import { updateFire } from "features/entity-caching/fires/thunks"
import store from "integrations/redux/store"
import { selectAllEntities } from "panels/content/side-panel/slice"
import { selectActivatedFire, selectActivatedFireId } from "../control/selectors"

export const getImageDateRange = (
    uploads: GenericUpload[] | undefined,
    dateFormat: DateFormat,
    timeFormat: TimeFormat
): [string | undefined, string | undefined] => {
    if (!uploads) return [undefined, undefined]

    const uploadsWithTime = uploads
        .filter((e) => !("loading" in e) && !("error" in e))
        .map((e) => (e.takenAt ? new Date(dateFromNumber(e.takenAt)) : null))
        .filter((e) => e) as Date[]
    if (!uploadsWithTime || uploadsWithTime.length === 0) return ["", ""]
    const minDate = uploadsWithTime.reduce((min, current) => (current < min ? current : min))
    const maxDate = uploadsWithTime.reduce((max, current) => (current > max ? current : max))
    const formattedDate = formatDateRange({
        value: { start: minDate, end: maxDate },
        dateFormat: dateFormat,
        timeFormat: timeFormat,
    })
    return [formattedDate.split(" -")[0], formattedDate.split("- ")[1]]
}

export const getDate = (
    createdAt: number | undefined,
    dateFormat: DateFormat,
    timeFormat: TimeFormat
) =>
    createdAt
        ? formatLongDate({
              value: new Date(createdAt * 1000),
              dateFormat: dateFormat,
              timeFormat: timeFormat,
          })
        : undefined

export const getDateFromDateString = (
    dateString: string | undefined,
    dateFormat: DateFormat,
    timeFormat: TimeFormat
) =>
    dateString
        ? formatLongDate({
              value: new Date(dateString),
              dateFormat: dateFormat,
              timeFormat: timeFormat,
          })
        : undefined

/**
 * Updates this project's DB lastJobFlightId db column if the provided
 * ID has a job that was updated more recently than the existing column
 * value's job.
 */
export const updateMostRecentJobId = () => {
    const state = store.getState()
    const project = selectActivatedFire(state)!
    const allEntities = selectAllEntities(state)
    const newestJobId = allEntities.reduce(
        (newestFlight, flight) => {
            const newJob = (flight as InfraredFlight | OrthoFlight)?.job

            // If this job is newer than the last job, return this flight.
            if (
                newJob &&
                newJob.createdAt &&
                newJob.createdAt > (newestFlight?.job?.createdAt || 0)
            )
                return flight as InfraredFlight | OrthoFlight

            // Otherwise return the last flight.
            return newestFlight
        },
        undefined as InfraredFlight | OrthoFlight | undefined
    )?.id

    // Overwrite the recentJobId if the new one doesn't match the old one.
    if (newestJobId !== project.lastJobFlightId) {
        const projectId = selectActivatedFireId(state)
        if (!projectId) return

        store.dispatch(updateFire({ id: projectId, updates: { lastJobFlightId: newestJobId } }))
    }
}

export const createDuplicatedEntityName = (name: string): string => {
    const split = name.split(" ")

    // If name ends in a number, increment it.
    if (/^-?\d+$/.test(split.at(-1) || "")) {
        const number = Number(split.at(-1)!)
        return split.slice(0, -1).join(" ") + " " + (number + 1)
    }

    // Otherwise, add "2" to the end.
    return name + " 2"
}
